// ADR CTV-732: super_admin-only Publica upload worklist.
//
// Publishers no longer place creative files into Publica's library — internal
// CTV staff do, and this is where they work. Publica exposes no upload API, so
// the transfer itself is a human step in Publica's own UI; the dashboard's job
// is the worklist and the verification, not the transfer.
//
// The flow per row: Download (saves under the name Publica will store it as),
// drop it into the station's creative library via the deep link, then Mark
// uploaded. Marking the last open task on a captured submission closes the
// later gate and starts the push.

import React from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Download,
  ExternalLink,
  Inbox,
  Loader2,
  Undo2,
} from 'lucide-react';
import { useAuth } from '../../components/self-service/auth/useAuth';
import { ApiError } from '../../lib/apiClient';
import {
  getCreativeViewUrl,
  listPublicaUploads,
  markPublicaUploaded,
  unmarkPublicaUploaded,
  type MarkUploadedResult,
  type PublicaUploadRow,
} from '../../services/publicaUploadsService';
import {
  formatFlightWindow,
  groupUploads,
  publicaLibraryUrl,
  uploadPillClass,
  uploadRowPills,
  type UploadPillKind,
} from './publicaUploadHelpers';

const PILL_LABEL: Record<UploadPillKind, string> = {
  blocking: 'Blocking',
  failedVerification: 'Failed verification',
};

/**
 * Which button on which row is mid-request. Scoped to the action and not just
 * the row: downloading a file is a read that has nothing to do with the mark,
 * so a slow download must not put a spinner on Mark uploaded (or the reverse).
 * Mark and Unmark do share a lock, since they write the same creative.
 */
type BusyAction = 'download' | 'mark' | 'unmark';
interface BusyState {
  creativeId: string;
  action: BusyAction;
}

/** Human summary of what a mark actually set in motion. */
function describeRelease(release: MarkUploadedResult['release']): string | null {
  if (!release?.released) return null;
  const parts: string[] = [];
  if (release.createEnqueued) parts.push('campaign push started');
  if (release.swapJobsEnqueued > 0) {
    parts.push(
      `${release.swapJobsEnqueued} creative ${release.swapJobsEnqueued === 1 ? 'change' : 'changes'} queued`,
    );
  }
  if (release.libraryMissJobsReset > 0) {
    parts.push(
      `${release.libraryMissJobsReset} failed ${release.libraryMissJobsReset === 1 ? 'push' : 'pushes'} re-queued`,
    );
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

const PublisherPublicaUploadsPage: React.FC = () => {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();

  const [rows, setRows] = React.useState<PublicaUploadRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState<BusyState | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);

  const isSuperAdmin = isAuthenticated && user?.rawRole === 'super_admin';

  const refresh = React.useCallback(
    async (opts?: { quiet?: boolean }) => {
      if (!isSuperAdmin) return;
      if (!opts?.quiet) setLoading(true);
      setError(null);
      try {
        const data = await listPublicaUploads();
        setRows(data.rows ?? []);
      } catch (e) {
        const msg =
          e instanceof ApiError
            ? e.message
            : e instanceof Error
              ? e.message
              : 'Failed to load Publica uploads';
        setError(msg);
      } finally {
        if (!opts?.quiet) setLoading(false);
      }
    },
    [isSuperAdmin],
  );

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleDownload = async (row: PublicaUploadRow) => {
    setBusy({ creativeId: row.creativeId, action: 'download' });
    setError(null);
    try {
      const { url } = await getCreativeViewUrl(row.submissionId, row.creativeId);
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      // Save under the normalized name so what lands on disk is what Publica
      // will store — that's what makes the push-time match succeed.
      a.download = row.normalizedFilename || row.filename || 'creative.mp4';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setBusy(null);
    }
  };

  const handleMark = async (row: PublicaUploadRow) => {
    setBusy({ creativeId: row.creativeId, action: 'mark' });
    setError(null);
    setNotice(null);
    try {
      const result = await markPublicaUploaded(row.creativeId);
      const released = describeRelease(result.release);
      setNotice(
        released
          ? `Marked ${row.filename} uploaded — ${released}.`
          : `Marked ${row.filename} uploaded.`,
      );
      await refresh({ quiet: true });
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Failed to mark uploaded';
      setError(msg);
    } finally {
      setBusy(null);
    }
  };

  const handleUnmark = async (row: PublicaUploadRow) => {
    setBusy({ creativeId: row.creativeId, action: 'unmark' });
    setError(null);
    setNotice(null);
    try {
      await unmarkPublicaUploaded(row.creativeId);
      setNotice(`Cleared the uploaded mark on ${row.filename}.`);
      await refresh({ quiet: true });
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Failed to unmark';
      setError(msg);
    } finally {
      setBusy(null);
    }
  };

  if (!isAuthenticated || !user) {
    return (
      <Navigate to="/admin" state={{ from: location.pathname + location.search }} replace />
    );
  }
  if (user.rawRole !== 'super_admin') {
    return <Navigate to="/admin" replace />;
  }

  const groups = groupUploads(rows);
  const blockingTotal = rows.filter((r) => r.blocking).length;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 md:px-8 py-8 md:py-12">
        <Link
          to="/admin"
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" /> Back to admin
        </Link>

        <div className="flex items-start justify-between mb-5 gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
              <Download className="w-5 h-5 text-blue-600" />
              Publica uploads
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Creatives waiting to be placed in a channel&rsquo;s Publica creative library.
              Download the file, add it to the library, then mark it uploaded.
            </p>
          </div>
          <button
            onClick={() => void refresh()}
            className="text-sm text-slate-600 hover:text-slate-900 shrink-0"
          >
            Refresh
          </button>
        </div>

        {blockingTotal > 0 && (
          <div className="mb-5 rounded-xl bg-red-50 border border-red-200 px-4 py-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
            <p className="text-sm text-red-800">
              <strong>
                {blockingTotal} paid {blockingTotal === 1 ? 'campaign is' : 'campaigns are'}
              </strong>{' '}
              waiting on {blockingTotal === 1 ? 'this upload' : 'these uploads'} before anything
              is created in Publica.
            </p>
          </div>
        )}

        {notice && (
          <div
            role="status"
            className="mb-5 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 flex items-start gap-2"
          >
            <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
            <p className="text-sm text-emerald-800">{notice}</p>
          </div>
        )}

        {error && (
          <div
            role="alert"
            className="mb-5 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800"
          >
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-10">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading Publica uploads&hellip;
          </div>
        ) : groups.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-white px-6 py-12 text-center">
            <Inbox className="w-8 h-8 text-slate-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-slate-700">Nothing waiting on an upload</p>
            <p className="text-sm text-slate-500 mt-1">
              Every creative on an open campaign is either in Publica&rsquo;s library already or
              marked uploaded.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {groups.map((org) => {
              const libraryUrl = publicaLibraryUrl(org.publicaPublisherId);
              return (
                <section
                  key={org.organizationId}
                  className="rounded-xl border border-slate-200 bg-white overflow-hidden"
                >
                  <header className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between gap-3">
                    <div>
                      <h2 className="text-sm font-semibold text-slate-900">
                        {org.organizationName}
                      </h2>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {org.count} {org.count === 1 ? 'file' : 'files'}
                        {org.blockingCount > 0 && ` · ${org.blockingCount} blocking`}
                      </p>
                    </div>
                    {libraryUrl && (
                      <a
                        href={libraryUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 hover:text-blue-900 shrink-0"
                      >
                        Publica creative library
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </header>

                  {org.stations.map((station) => (
                    <div key={station.stationId}>
                      <div className="px-4 pt-4 pb-1 flex items-baseline gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          {station.stationName}
                        </span>
                        <span className="text-xs text-slate-400">
                          {station.count} {station.count === 1 ? 'file' : 'files'}
                        </span>
                      </div>

                      {station.submissions.map((submission) => (
                        <div key={submission.submissionId} className="px-4 pb-2">
                          {/* Submission context: the admin is putting one
                              campaign's files into the library together, and
                              the later gate opens per submission. */}
                          <div className="mt-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  {submission.stationSlug ? (
                                    <Link
                                      to={`/admin/${submission.stationSlug}/submissions/${submission.submissionId}`}
                                      className="text-sm font-semibold text-slate-900 hover:text-blue-700 truncate"
                                    >
                                      {submission.campaignName ?? 'Campaign'}
                                    </Link>
                                  ) : (
                                    <span className="text-sm font-semibold text-slate-900 truncate">
                                      {submission.campaignName ?? 'Campaign'}
                                    </span>
                                  )}
                                  <span
                                    className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                      submission.blocking
                                        ? uploadPillClass('blocking')
                                        : 'bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200'
                                    }`}
                                  >
                                    {submission.blocking ? 'Blocking' : 'In review'}
                                  </span>
                                </div>
                                <p className="text-xs text-slate-500 mt-0.5">
                                  {submission.advertiserName ?? 'Advertiser'}
                                  {formatFlightWindow(submission.startDate, submission.endDate)
                                    ? ` · ${formatFlightWindow(submission.startDate, submission.endDate)}`
                                    : ''}
                                  {` · ${submission.rows.length} ${submission.rows.length === 1 ? 'file' : 'files'} waiting`}
                                </p>
                              </div>
                            </div>
                          </div>

                          <ul className="divide-y divide-slate-100">
                            {submission.rows.map((row) => {
                          const rowBusy = busy?.creativeId === row.creativeId ? busy.action : null;
                          const downloading = rowBusy === 'download';
                          // Mark and Unmark write the same row, so each waits
                          // on the other; neither waits on a download.
                          const marking = rowBusy === 'mark';
                          const unmarking = rowBusy === 'unmark';
                          const writing = marking || unmarking;
                          const marked = !!row.publicaUploadedAt;
                          return (
                            <li
                              key={row.creativeId}
                              className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3"
                            >
                              <div className="min-w-0 flex-1">
                                <p className="font-mono text-xs text-slate-900 break-all">
                                  {row.normalizedFilename}
                                </p>
                                {row.normalizedFilename !== row.filename && (
                                  <p className="font-mono text-[11px] text-slate-400 break-all mt-0.5">
                                    filed as {row.filename}
                                  </p>
                                )}
                                <div className="flex flex-wrap gap-1.5 mt-1.5">
                                  {uploadRowPills(row).map((pill) => (
                                    <span
                                      key={pill}
                                      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${uploadPillClass(pill)}`}
                                    >
                                      {PILL_LABEL[pill]}
                                    </span>
                                  ))}
                                </div>
                              </div>

                              <div className="flex items-center gap-2 shrink-0">
                                <button
                                  type="button"
                                  onClick={() => void handleDownload(row)}
                                  disabled={downloading || !row.storagePath}
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-300 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                                >
                                  {downloading ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  ) : (
                                    <Download className="w-3.5 h-3.5" />
                                  )}
                                  {downloading ? 'Downloading…' : 'Download'}
                                </button>
                                {marked ? (
                                  <button
                                    type="button"
                                    onClick={() => void handleUnmark(row)}
                                    disabled={writing}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-300 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                                  >
                                    {unmarking ? (
                                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    ) : (
                                      <Undo2 className="w-3.5 h-3.5" />
                                    )}
                                    Unmark
                                  </button>
                                ) : null}
                                <button
                                  type="button"
                                  onClick={() => void handleMark(row)}
                                  disabled={writing}
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-900 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50"
                                >
                                  {marking ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  ) : (
                                    <CheckCircle2 className="w-3.5 h-3.5" />
                                  )}
                                  {marked ? 'Re-mark uploaded' : 'Mark uploaded'}
                                </button>
                              </div>
                            </li>
                              );
                            })}
                          </ul>
                        </div>
                      ))}
                    </div>
                  ))}
                </section>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PublisherPublicaUploadsPage;
